import { useState, useCallback } from 'react';

import { returnActivityToQueue } from '../api';

export type UseReturnActivityToQueueResult = {
  returnToQueue: (activityId: number) => Promise<boolean>;
  isLoading: boolean;
  error: string | null;
  clearError: () => void;
};

/**
 * Hook for returning an activity to the queue.
 * Returns true if successful, false on error.
 * Calls onSuccess callback when activity is successfully returned.
 */
const useReturnActivityToQueue = (onSuccess?: () => void): UseReturnActivityToQueueResult => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const returnToQueue = useCallback(async(activityId: number): Promise<boolean> => {
    setIsLoading(true);
    setError(null);
    try {
      await returnActivityToQueue(activityId);
      onSuccess?.();
      return true;
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to return activity to queue';
      setError(message);
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [onSuccess]);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return { returnToQueue, isLoading, error, clearError };
};

export default useReturnActivityToQueue;
